import { useEffect, useMemo, useRef, useState } from 'react'
import {
  GRID_AGGREGATION_FUNCTIONS,
  GridAggregationFunction,
  GridAggregationGetCellValueParams,
  GridEventListener,
  GridGroupNode,
  GridRowId,
  useGridApiRef,
  useKeepGroupedColumnsHidden,
} from '@mui/x-data-grid-premium'
import { GridColDef } from '@mui/x-data-grid'
import { Table } from 'Common/MUITable/MUITable'
import { autoHeight } from 'Common/utils/utils'
import { Box, IconButton, Typography } from '@mui/material'
import { darken } from '@mui/material/styles'
import {
  AdherenceReportData,
  AdherenceReportProps,
} from './AdherenceReport.types'
import { min, max } from 'lodash'
import { KeyboardArrowDown, KeyboardArrowRight } from '@mui/icons-material'

export const AdherenceTable = <T extends AdherenceReportData>({
  data,
  isLoading,
  apiRef,
}: AdherenceReportProps<T>) => {
  const [expandedGroupIds, setExpandedGroupIds] = useState<GridRowId[]>([])

  useEffect(() => {
    const handleEvent: GridEventListener<'rowExpansionChange'> = (
      params: GridGroupNode,
      event,
      details
    ) => {
      const { children, childrenExpanded, parent } = params
      if (childrenExpanded && parent !== 'auto-generated-group-node-root') {
        setExpandedGroupIds(children)
      }
    }

    return apiRef.current.subscribeEvent('rowExpansionChange', handleEvent)
  }, [apiRef])

  const cacheRefConstrained = useRef(new Map<GridRowId, string>())
  const cacheRefUnconstrained = useRef(new Map<GridRowId, string>())

  useEffect(() => {
    cacheRefConstrained.current.clear()
    cacheRefUnconstrained.current.clear()
  }, [data])

  const groupedConstrainedAdherence: GridAggregationFunction<
    {
      actualStock: number
      constrainedStock: number
    },
    number
  > = {
    label: '',
    getCellValue: ({ row }: GridAggregationGetCellValueParams) => ({
      actualStock: row.weekStartInventory,
      constrainedStock: row.constrainedStockTarget,
    }),
    apply: ({ values, groupId }) => {
      if (cacheRefConstrained.current.has(groupId)) {
        return cacheRefConstrained.current.get(groupId)!
      }

      let totalActualStock: number = 0
      let totalConstrainedStock: number = 0

      if (values) {
        values.forEach(
          (
            value: { actualStock: number; constrainedStock: number } | undefined
          ): void => {
            if (value) {
              totalActualStock += value.actualStock
              totalConstrainedStock += value.constrainedStock
            }
          }
        )
      }

      let result: number = 0
      if (totalConstrainedStock !== 0) {
        result =
          (totalActualStock - totalConstrainedStock) / totalConstrainedStock
      }

      let value = `${(result * 100).toFixed(2)} %`
      cacheRefConstrained.current.set(groupId, value)

      return value
    },
  }

  const groupedUnconstrainedAdherence: GridAggregationFunction<
    {
      actualStock: number
      unconstrainedStock: number
    },
    number
  > = {
    label: '',
    getCellValue: ({ row }: GridAggregationGetCellValueParams) => ({
      actualStock: row.weekStartInventory,
      unconstrainedStock: row.unconstrainedStockTarget,
    }),
    apply: ({ values, groupId }) => {
      if (cacheRefUnconstrained.current.has(groupId)) {
        return cacheRefUnconstrained.current.get(groupId)!
      }

      let totalActualStock: number = 0
      let totalUnconstrainedStock: number = 0

      if (values) {
        values.forEach(
          (
            value:
              | { actualStock: number; unconstrainedStock: number }
              | undefined
          ): void => {
            if (value) {
              totalActualStock += value.actualStock
              totalUnconstrainedStock += value.unconstrainedStock
            }
          }
        )
      }

      let result: number = 0
      if (totalUnconstrainedStock !== 0) {
        result =
          (totalActualStock - totalUnconstrainedStock) / totalUnconstrainedStock
      }

      let value = `${(result * 100).toFixed(2)} %`
      cacheRefUnconstrained.current.set(groupId, value)

      return value
    },
  }

  const initialState = useKeepGroupedColumnsHidden({
    apiRef,
    initialState: {
      columns: {
        // Cycle / Replenishment Type are hidden until the COUPLED SKU data
        // gap (should read from the Production Plan Template) is fixed.
        columnVisibilityModel: {
          cycleTime: false,
          replenishmentTypeCode: false,
        },
      },
      rowGrouping: {
        model: ['siteCode', 'skuCode'],
      },
      sorting: {
        sortModel: [{ field: 'id', sort: 'asc' }],
      },
      aggregation: {
        model: {
          unconstrainedStockTarget: 'avg',
          constrainedStockTarget: 'avg',
          weekStartInventory: 'avg',
          adherenceConstrained: 'groupedConstrainedAdherence',
          adherenceUnconstrained: 'groupedUnconstrainedAdherence',
        },
      },
      pagination: { paginationModel: { pageSize: 100 } },
    },
  })

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'isoYear',
        headerName: 'ISO Year',
        headerAlign: 'center',
        align: 'center',
        width: 100,
        editable: false,
      },
      {
        field: 'isoWeek',
        headerName: 'ISO Week',
        headerAlign: 'center',
        align: 'center',
        width: 100,
        editable: false,
      },
      {
        field: 'primaryWorkCentreCode',
        headerName: 'Primary WC Code',
        headerAlign: 'center',
        align: 'center',
        width: 100,
        editable: false,
      },
      {
        field: 'cycleTime',
        headerName: 'Cycle',
        headerAlign: 'center',
        align: 'center',
        width: 100,
        editable: false,
      },
      {
        field: 'replenishmentTypeCode',
        headerName: 'Replenishment Type',
        headerAlign: 'center',
        align: 'center',
        width: 100,
        editable: false,
      },
      {
        field: 'skuCode',
        headerName: 'SKU',
        flex: 1,
        editable: false,
      },
      {
        field: 'siteCode',
        headerName: 'Location',
        flex: 1,
        editable: false,
      },
      {
        field: 'unconstrainedStockTarget',
        headerName: 'Unconstrained Stock Target',
        type: 'number',
        flex: 1,
        editable: false,
      },
      {
        field: 'constrainedStockTarget',
        headerName: 'Constrained Stock Target',
        type: 'number',
        flex: 1,
        editable: false,
      },
      {
        field: 'weekStartInventory',
        headerName: 'Actual Stock',
        type: 'number',
        flex: 1,
        editable: false,
      },
      {
        field: 'adherenceUnconstrained',
        headerName: 'Adherence to Unconstrained Target',
        flex: 1,
        type: 'number',
        valueFormatter: (value: number) => {
          if (isNaN(value)) {
            return ''
          }
          return `${(value * 100).toFixed(2)} %`
        },
        renderCell: (params) => {
          if (params.rowNode.type !== 'group') {
            if (params.formattedValue === '') {
              return '--'
            } else {
              return params.formattedValue
            }
          } else {
          }
          return params.value
        },

        editable: false,
      },
      {
        field: 'adherenceConstrained',
        headerName: 'Adherence to Constrained Target',
        flex: 1,
        type: 'number',
        valueFormatter: (value: number) => {
          if (isNaN(value)) {
            return ''
          }
          return `${(value * 100).toFixed(2)} %`
        },
        renderCell: (params) => {
          if (params.rowNode.type !== 'group') {
            if (params.formattedValue === '') {
              return '--'
            } else {
              return params.formattedValue
            }
          }
          return params.value
        },

        editable: false,
      },
    ],

    [data]
  )

  return (
    <Box>
      <Box flexDirection="row"></Box>
      <Box
        id="#table-container-mui"
        sx={{
          maxWidth: 'calc(100vw - 70px)',
          '& .MuiDataGrid-cell--editable': {
            backgroundColor: '#CBD5E1',
          },
          '& .MuiBox-root': {
            padding: '0px !important',
          },
        }}
      >
        <Table
          sx={{
            '& .MuiDataGrid-cell:focus': {
              outline: 'none !important',
              boxShadow: 'none !important',
            },
            '& .MuiDataGrid-cell:focus-within': {
              outline: 'none !important',
            },
            // Optional: prevent focus ring on column headers too
            '& .MuiDataGrid-columnHeader:focus': {
              outline: 'none !important',
            },
            '& .MuiDataGrid-columnHeader:focus-within': {
              outline: 'none !important',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'transparent !important',
            },
            '& .MuiDataGrid-row.row--edited': {
              backgroundColor: (theme) => {
                if (theme.palette.mode === 'light') {
                  return 'rgba(255, 254, 176, 0.3)'
                }
                return darken('rgba(255, 254, 176, 1)', 0.6)
              },
            },
            '& .group-header-row': {
              fontSize: '0.95rem',
              backgroundColor: 'white',
              color: '#0366d6',
              position: 'relative',
              zIndex: 1,
              '&:hover': {
                backgroundColor: 'white !important',
              },
            },
            '& .group-footer-row': {
              borderTop: '1px solid lightgrey',
              borderBottom: '2px solid lightgrey',
              fontWeight: 'bold',
              fontSize: '1rem',
              backgroundColor: 'white',
              position: 'relative',
              zIndex: 1,
              '&:hover': {
                backgroundColor: 'white !important',
              },
            },
            '& .data-row': {
              position: 'relative',
              zIndex: 0,
            },
            '& .data-row.min': {
              boxShadow: 'inset 0 2px 8px -2px rgba(0, 0, 0, 0.1)',
            },
            '& .data-row.max': {
              boxShadow: 'inset 0 -2px 6px -2px rgba(0, 0, 0, 0.1)',
            },
            '& .data-row.min.max': {
              boxShadow: `
                  inset 0 2px 8px -2px rgba(0, 0, 0, 0.1),
                  inset 0 -2px 8px -2px rgba(0, 0, 0, 0.1)
                  `,
            },
          }}
          slotProps={{
            loadingOverlay: {
              variant: 'linear-progress',
              noRowsVariant: 'linear-progress',
            },
          }}
          ref={autoHeight}
          apiRef={apiRef}
          disableRowSelectionOnClick
          hideFooter={true}
          loading={isLoading}
          rows={data ?? []}
          columns={columns}
          pagination
          initialState={initialState}
          groupingColDef={{
            hideDescendantCount: true,
            width: 220,
            headerName: 'Site / sku code',
            renderCell: (params) => {
              if (params.rowNode.type === 'group') {
                const { rowNode } = params
                const { depth } = rowNode
                const isExpanded = rowNode?.childrenExpanded ?? false

                const handleToggle = (event: MouseEvent) => {
                  event.stopPropagation()
                  apiRef.current.setRowChildrenExpansion(params.id, !isExpanded)
                }

                return (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      paddingLeft: depth === 0 ? '0px' : '8px',
                      width: '100%',
                      boxSizing: 'border-box',
                      '&:focus': {
                        outline: 'none !important',
                        boxShadow: 'none !important',
                      },
                    }}
                  >
                    <Box
                      display="flex"
                      sx={{ marginLeft: depth === 1 ? '36px' : '0px' }}
                    >
                      <IconButton
                        size="small"
                        onClick={handleToggle}
                        aria-label={
                          isExpanded ? 'Collapse group' : 'Expand group'
                        }
                      >
                        {isExpanded ? (
                          <KeyboardArrowDown />
                        ) : (
                          <KeyboardArrowRight />
                        )}
                      </IconButton>
                    </Box>
                    <Box display="flex">
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 'bold', ml: 1, color: 'grey' }}
                      >
                        {params.value}
                      </Typography>
                    </Box>
                  </Box>
                )
              }

              const { skuCode, siteCode } = params.row

              return (
                <Box
                  display="flex"
                  flexDirection="row"
                  justifyContent="flex-start"
                  gap="20px"
                  alignItems="center"
                  sx={{
                    padding: '4px',
                    height: '100%',
                    marginLeft: '20px',
                  }}
                >
                  <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                    sx={{
                      border: '2px solid lightgrey',
                      borderRadius: '4px',
                      padding: '4px 4px',
                      height: '70%',
                      width: '40px',
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 'bold',
                        fontSize: '0.7rem',
                        color: 'grey',
                      }}
                    >
                      {siteCode}
                    </Typography>
                  </Box>
                  <Typography
                    variant="subtitle2"
                    sx={(theme) => ({
                      fontWeight: 'bold',
                      color: theme.palette.primary.main,
                      fontSize: '0.8rem',
                    })}
                  >
                    {skuCode}
                  </Typography>
                </Box>
              )
            },
          }}
          getRowClassName={(params) => {
            const isGroupHeader =
              typeof params.id === 'string' &&
              params.id.startsWith('auto-generated-row-')

            if (isGroupHeader) {
              return 'group-header-row'
            }

            if (
              typeof params.id === 'string' &&
              params.id.startsWith('auto-generated-group-footer')
            ) {
              return 'group-footer-row'
            }

            let classes = ['data-row']
            if (
              typeof params.id === 'number' &&
              params.id === min(expandedGroupIds)
            ) {
              classes.push('min')
            }
            if (
              typeof params.id === 'number' &&
              params.id === max(expandedGroupIds)
            ) {
              classes.push('max')
            }

            return classes.join(' ')
          }}
          getAggregationPosition={(groupNode) =>
            groupNode == null ? null : 'inline'
          }
          aggregationFunctions={{
            ...GRID_AGGREGATION_FUNCTIONS,
            groupedConstrainedAdherence,
            groupedUnconstrainedAdherence,
          }}
          pageSizeOptions={[25, 50, 100]}
          density="compact"
          disableColumnSorting
        />
      </Box>
    </Box>
  )
}
